import {
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type ClipboardEvent,
  type FormEvent,
  type KeyboardEvent,
} from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router";
import { Alert, Button, useToast } from "@/ui";
import { SessionExpiredNotice } from "@/auth/SessionExpiredNotice";
import { AuthLayout } from "@/layout/AuthLayout";
import { useAuth } from "@/auth/AuthProvider";
import { homeFor } from "@/auth/RequireAuth";
import { useTenant } from "@/tenant/TenantProvider";
import { api, ApiClientError } from "@/lib/api";
import { useLanguage } from "@/i18n/language";
import { otpErrorText } from "@/lib/karigarPoints";
import { InstallAppButton } from "@/components/InstallAppButton";
import { isDemoOtpAccount } from "@gatiq/shared";

const RESEND_SECONDS = 30;

/**
 * Karigar sign-in: mobile + OTP (D-06). Bilingual on every label (D-11),
 * Hindi first — this is the only screen a karigar sees before their app.
 */
export function KarigarLogin() {
  const { orgSlug = "" } = useParams<{ orgSlug: string }>();
  const { loginWithOtp } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { payload, error: tenantError, loading: tenantLoading } = useTenant();
  const { t } = useLanguage("hi");

  // Remember which org the user is signing in to for PWA launch redirect.
  useEffect(() => {
    if (orgSlug) localStorage.setItem("gatiq-last-org", orgSlug);
  }, [orgSlug]);

  const [step, setStep] = useState<"mobile" | "otp">("mobile");
  const [phone, setPhone] = useState("");
  const CODE_LENGTH = isDemoOtpAccount(orgSlug, `+91${phone}`) ? 5 : 6;
  const [digits, setDigits] = useState<string[]>(Array(CODE_LENGTH).fill(""));
  const [fields, setFields] = useState<Record<string, string>>({});
  const [requesting, setRequesting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [devCode, setDevCode] = useState<string | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(0);

  const boxRefs = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const timer = window.setInterval(() => {
      setSecondsLeft((s) => Math.max(0, s - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [secondsLeft]);

  async function requestCode(): Promise<void> {
    setFields({});
    setRequesting(true);
    try {
      const result = await api.post<{ ok: boolean; devCode?: string }>(
        `/auth/${orgSlug}/otp/request`,
        { phone: `+91${phone}` },
      );
      setDevCode(result.devCode ?? null);
      setDigits(Array(CODE_LENGTH).fill(""));
      setStep("otp");
      setSecondsLeft(RESEND_SECONDS);
    } catch (err) {
      if (err instanceof ApiClientError) setFields(err.fields ?? {});
      const text = otpErrorText(err);
      toast.error(t(text.hi, text.en));
    } finally {
      setRequesting(false);
    }
  }

  async function handleMobileSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    await requestCode();
  }

  async function handleResend(): Promise<void> {
    if (secondsLeft > 0 || requesting) return;
    await requestCode();
  }

  function setDigitAt(index: number, value: string): void {
    setDigits((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  }

  function handleDigitChange(index: number, event: ChangeEvent<HTMLInputElement>): void {
    const raw = event.target.value.replace(/\D/g, "");
    if (!raw) {
      setDigitAt(index, "");
      return;
    }
    const value = raw.slice(-1);
    setDigitAt(index, value);
    if (index < CODE_LENGTH - 1) {
      boxRefs.current[index + 1]?.focus();
    }
  }

  function handleDigitKeyDown(index: number, event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === "Backspace" && !digits[index] && index > 0) {
      boxRefs.current[index - 1]?.focus();
    }
  }

  function handleDigitPaste(event: ClipboardEvent<HTMLInputElement>): void {
    const text = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, CODE_LENGTH);
    if (!text) return;
    event.preventDefault();
    const next = Array(CODE_LENGTH).fill("");
    for (let i = 0; i < text.length; i++) next[i] = text[i]!;
    setDigits(next);
    boxRefs.current[Math.min(text.length, CODE_LENGTH - 1)]?.focus();
  }

  async function handleVerifySubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    const code = digits.join("");
    if (code.length !== CODE_LENGTH) return;

    setFields({});
    setVerifying(true);
    try {
      const user = await loginWithOtp(orgSlug, `+91${phone}`, code);
      /*
       * A karigar who scanned a sticker before signing in was sent here with
       * the sticker's URL remembered. Dropping them on the home screen instead
       * would lose the code and make them hunt for the product again.
       *
       * Only in-app paths are honoured — a full URL in router state would be
       * an open redirect, and this screen is reachable from a printed label
       * anyone can put in front of someone.
       */
      const from = (location.state as { from?: unknown } | null)?.from;
      const back = typeof from === "string" && /^\/[^/]/.test(from) ? from : null;
      navigate(back ?? homeFor(user), { replace: true });
    } catch (err) {
      if (err instanceof ApiClientError) setFields(err.fields ?? {});
      const text = otpErrorText(err);
      toast.error(t(text.hi, text.en));
    } finally {
      setVerifying(false);
    }
  }

  const brand = payload.branding;
  const phoneValid = /^[6-9]\d{9}$/.test(phone);
  const codeComplete = digits.every((d) => d !== "");

  return (
    <AuthLayout
      accent={t("अंक कमाएँ।", "earn points.")}
      blurb={
        brand.tagline ||
        t(
          "हर प्रोडक्ट स्कैन पर अंक कमाएँ और अपने काउंटर पर रिडीम करें।",
          "Earn points on every product scan and redeem them at your counter.",
        )
      }
      headline={t("स्कैन करें,", "Scan,")}
      languageSwitch
      points={[
        t("क्यूआर स्कैन करें, अंक पाएँ", "Scan a QR, earn points"),
        t("किसी भी काउंटर पर रिडीम करें", "Redeem at your counter"),
        t("अपने अंक कभी भी देखें", "Check your balance any time"),
      ]}
    >
      <div className="relative overflow-hidden rounded-lg bg-raised px-7 pt-8 pb-7 shadow-pop ring-1 ring-black/[0.06] sm:px-8">
        <span aria-hidden="true" className="absolute inset-x-0 top-0 h-1 bg-brand" />
        <div className="flex flex-col gap-6">
          <SessionExpiredNotice bilingual />
          {tenantError && !tenantLoading ? (
            <Alert kind="danger">{tenantError}</Alert>
          ) : step === "mobile" ? (
            <form className="flex flex-col gap-4" noValidate onSubmit={handleMobileSubmit}>
              <h1 className="font-title text-2xl font-semibold tracking-[-0.02em] text-ink">
                {t("लॉगिन करें", "Sign in")}
              </h1>

              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-ink" htmlFor="karigar-phone">
                  {t("मोबाइल नंबर", "Mobile number")}
                </label>
                <div className="flex gap-2">
                  <span className="flex h-11 shrink-0 items-center rounded-md border border-rule bg-surface px-3 text-sm font-medium text-muted">
                    +91
                  </span>
                  <input
                    autoComplete="tel-national"
                    autoFocus
                    className="h-11 w-full flex-1 rounded-md border border-rule bg-raised px-3 text-ink shadow-control transition-[border-color,box-shadow] placeholder:text-muted/60 hover:border-ink/25 focus:border-brand focus:shadow-[0_0_0_3px_var(--gq-color-1-ring)] focus:outline-none"
                    id="karigar-phone"
                    inputMode="numeric"
                    maxLength={10}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                    placeholder="98765 43210"
                    value={phone}
                  />
                </div>
                {fields.phone ? (
                  <p className="text-sm text-danger" role="alert">
                    {fields.phone}
                  </p>
                ) : null}
              </div>

              <Button block disabled={!phoneValid} loading={requesting} size="lg" type="submit">
                {t("OTP भेजें", "Send OTP")}
              </Button>

              <p className="text-center text-xs text-muted">
                {CODE_LENGTH === 5 ? t("डेमो खाते का OTP: 00000", "Demo account OTP: 00000") : t("OTP आपके WhatsApp पर आएगा", "The OTP will arrive on WhatsApp")}
              </p>

              {/*
                A karigar who is really a sales rep or counter staff, or someone
                who came from a printed QR and needs the staff screen instead,
                has no way back from here — the OTP form takes over the whole
                card. Put the staff sign-in on the first step too, where they
                land, rather than only after they have already asked for a code.
              */}
              <Link
                className="text-center text-xs text-muted hover:underline"
                to={`/${orgSlug}/login`}
              >
                {t("स्टाफ़ हैं? ईमेल से लॉगिन करें", "Staff? Sign in with email")}
              </Link>

              <InstallAppButton label={t("ऐप इंस्टॉल करें", "Install app")} />
            </form>
          ) : (
            <form className="flex flex-col gap-4" noValidate onSubmit={handleVerifySubmit}>
              <div className="text-center">
                <h1 className="font-title text-xl font-semibold text-ink">{t("कोड दर्ज करें", "Enter the code")}</h1>
                <p className="mt-1 text-sm text-muted">+91 {phone}</p>
              </div>

              {CODE_LENGTH === 5 ? <Alert kind="info">{t("डेमो खाते का OTP", "Demo account OTP")}: <strong>00000</strong></Alert> : devCode ? (
                <Alert kind="info" title="Dev mode — no WhatsApp bridge yet">
                  {t("आपका कोड", "Your code")}: <strong>{devCode}</strong>
                </Alert>
              ) : null}

              <div className="flex flex-col gap-1.5">
                <span className="text-sm font-medium text-ink">{t(`${CODE_LENGTH} अंकों का कोड`, `${CODE_LENGTH}-digit code`)}</span>
                <div className="flex justify-between gap-2">
                  {digits.map((digit, index) => (
                    <input
                      aria-label={`Digit ${index + 1}`}
                      className="h-13 w-11 rounded-md border border-rule bg-raised text-center font-title text-xl font-semibold text-ink shadow-control transition-[border-color,box-shadow] focus:border-brand focus:shadow-[0_0_0_3px_var(--gq-color-1-ring)] focus:outline-none"
                      inputMode="numeric"
                      key={index}
                      maxLength={1}
                      onChange={(e) => handleDigitChange(index, e)}
                      onKeyDown={(e) => handleDigitKeyDown(index, e)}
                      onPaste={handleDigitPaste}
                      ref={(el) => {
                        boxRefs.current[index] = el;
                      }}
                      value={digit}
                    />
                  ))}
                </div>
                {fields.code ? (
                  <p className="text-sm text-danger" role="alert">
                    {fields.code}
                  </p>
                ) : null}
              </div>

              <Button block disabled={!codeComplete} loading={verifying} size="lg" type="submit">
                {t("पुष्टि करें", "Verify")}
              </Button>

              <div className="flex items-center justify-center gap-1 text-sm">
                <button
                  className="text-brand-ink disabled:cursor-not-allowed disabled:text-muted disabled:no-underline"
                  disabled={secondsLeft > 0 || requesting}
                  onClick={handleResend}
                  type="button"
                >
                  {secondsLeft > 0
                    ? t(`${secondsLeft} सेकंड में फिर भेजें`, `Resend in ${secondsLeft}s`)
                    : t("कोड फिर भेजें", "Resend code")}
                </button>
              </div>

              <button
                className="text-center text-xs text-muted hover:underline"
                onClick={() => setStep("mobile")}
                type="button"
              >
                {t("नंबर बदलें", "Change number")}
              </button>

              <Link
                className="text-center text-xs text-muted hover:underline"
                to={`/${orgSlug}/login`}
              >
                {t("ईमेल से लॉगिन करें", "Sign in with email")}
              </Link>
            </form>
          )}

        </div>
      </div>
    </AuthLayout>
  );
}
